import { Observable, Observer, Subject } from "rxjs";
import { DependencyItem } from "../types/DependencyItem.interface";
import { Store, select } from "@ngrx/store";
import { takeUntil, filter } from "rxjs/operators";

/**
 * DependencyItem kind of Observables
 * Beacuse we need a "terminate" method
 */
export class DependencyObservable<T> extends Observable<DependencyItem<T>> {
    /**
     * List of self instances
     */
    private static instances: DependencyObservable<any>[] = [];

    /**
     * Whether broadcasting is active or not
     */
    private paused = false;

    /**
     * Last received value (used when broadcasting is paused)
     */
    protected lastReceivedValue: T | Error = null;

    /**
     * Dependency item
     */
    private dependency: DependencyItem<T>;

    /**
     * List of observers, in other words, is the list
     * of subscribers
     */
    private observers: Observer<DependencyItem<T>>[] = [];

    /**
     * This is used to control the subscription to the store
     */
    private mainSubject: Subject<T>;

    /**
     * Maps
     */
    private maps: ((val: DependencyItem<T>) => DependencyItem<T>)[] = [];

    /**
     * Creates a new instance (singleton implementation)
     */
    public static create<D>(store: Store<any>, dependency: DependencyItem<D>): DependencyObservable<D> {
        const len = DependencyObservable.instances.push(new DependencyObservable<D>(store, dependency));
        return DependencyObservable.instances[len - 1];
    }

    /**
     * Pauses notifications on all Observers
     */
    public static pauseAll() {
        for (const ob of DependencyObservable.instances) {
            ob.pause();
        }
    }

    /**
     * Resumes notifications on all Observers
     */
    public static resumeAll() {
        for (const ob of DependencyObservable.instances) {
            ob.resume();
        }
    }

    /**
     * Resumes notifications on all Observers
     */
    public static destroyAll() {
        for (const ob of DependencyObservable.instances) {
            ob.destroy(false);
        }
        DependencyObservable.instances = [];
    }

    /**
     * For a given dependency this functions returns an Observable initializer function
     * The "Observable initializer function" is the function that is executed every time
     * anyone subscribes to your Observable instance.
     * This "Observable initializer function" has a special behavior that prevents the
     * observable from creating a new independent execution for each subscribed observer.
     *
     * @param dependency The dependency the obervable refers to
     */
    private constructor(store: Store<any>, dependency: DependencyItem<T>) {
        super(
            /**
             * This is the Observable initializer function. The "Observable
             * initializer function" is the function that is executed every
             * time one subscribes to this Observable instance.
             * This Observable uses a tecnic called multicasting that prevents the
             * observable from creating a new independent execution for each subscribed observer.
             * Please see: https://angular.io/guide/observables#multicasting
             */
            (observer: Observer<DependencyItem<T>>) => {
                this.observers.push(observer);

                // When this is the first subscription, start
                // the subscription in the store
                if (this.observers.length === 1) {
                    // we have to stop subscription if we haven't listeners
                    // we use this subject for that
                    this.mainSubject = new Subject();

                    const notEmptyFilter = (val: any) => {
                        if (val !== null && val !== undefined) {
                            if (typeof val.balance !== "undefined") {
                                // balance exception
                                return val.dateFrom !== null;
                            } else if (typeof val.invoices !== "undefined") {
                                // invoices exception
                                return val.invoices !== null;
                            }

                            return true;
                        }

                        return false;
                    };

                    const completeCallback = this.terminate.bind(this);


                    if (!this.dependency.selector) {
                        // this should not happen in production, if it does happen in development,
                        // you've probably forgot to add a selector to the Dependency in the DepencyList
                        console.warn("Unexpected type 'null' in dependecy.selector, expecting an NgRx Selector");
                    } else {
                        // subscribing to changes
                        store.pipe(
                            takeUntil(this.mainSubject),
                            select(this.dependency.selector)
                        // tslint:disable-next-line: deprecation
                        ).subscribe((depValue: T) => {
                            // double check to not broadcast empty values
                            // apparently this is needed, since filter isn't
                            // working on all cases - only God knows why
                            if (notEmptyFilter(depValue)) {
                                this.broadcastValue(depValue);
                            }
                        }, this.broadcastValue.bind(this), completeCallback);
                    }

                    if (!this.dependency.errorSelector) {
                        // this should not happen in production, if it does happen in development,
                        // you've probably forgot to add an error selector to the Dependency in the DepencyList
                        console.warn("Unexpected type 'null' in dependecy.errorSelector, expecting an NgRx Selector");
                    } else {
                        // subscribing to errors
                        store.pipe(
                            takeUntil(this.mainSubject),
                            select(this.dependency.errorSelector),
                            filter(notEmptyFilter)
                        // tslint:disable-next-line: deprecation
                        ).subscribe(
                            this.broadcastValue.bind(this),
                            this.broadcastValue.bind(this),
                            completeCallback);
                    }
                }


                return {
                    unsubscribe: () => {
                        // Remove from the observers array so it's no longer notified
                        this.observers.splice(this.observers.indexOf(observer), 1);
                        // If there's no more listeners, unsubscribe from
                        // the store
                        if (this.observers.length < 1) {
                            this.unplugFromStore();
                        }
                    }
                };
            }
        );

        this.dependency = dependency;
        this.pipe();
    }

    /**
     * Add a map function only once, repeated maps will be ignored
     *
     * @param mapFunction Map function
     */
    public addInterceptor(mapFunction: (val: DependencyItem<T>) => DependencyItem<T>) {
        this.maps.push(mapFunction);
        return this;
    }

    /**
     * Add a map function only once, repeated maps will be ignored
     *
     * @param mapFunction Map function
     */
    public addInterceptorOnce(mapFunction: (val: DependencyItem<T>) => DependencyItem<T>) {
        for (const fn of this.maps) {
            if (mapFunction === fn || mapFunction.toString() === fn.toString()) {
                return this;
            }
        }

        this.addInterceptor(mapFunction);
        return this;
    }

    /**
     * Broadcast a value to all of the observers
     *
     * @param depValue Value to broadcast
     * @param allowRecursion Allow a new call to this function again, set it
     * to false to prevent an endless loop (default is true - allow a new call)
     */
    private broadcastValue(depValue: T | Error, allowRecursion = true): void {
        if (this.paused) {
            // broadcasting is paused
            this.lastReceivedValue = depValue;
            return ;
        }

        if (depValue instanceof Error) {
            if (allowRecursion && this.dependency.onFail) {
                return this.broadcastValue.call(this, this.dependency.onFail.call(this.dependency, depValue), false);
            }

            // broadcasting the dependency to all of error listners
            this.observers.forEach((obs: Observer<DependencyItem<T>>) => obs.error(depValue));
        } else {
            let dependency = { ...this.dependency, data: depValue };
            for (const map of this.maps) {
                dependency = map(dependency);
                if (this.paused) {
                    return this.broadcastValue(depValue);
                }
            }

            // make change definitive
            this.dependency.data = dependency.data;

            // broadcasting the dependency to all of our listners
            this.observers.forEach((obs: Observer<DependencyItem<T>>) => obs.next(this.dependency));
        }

    }

    /**
     * Pause broadcasting
     */
    public pause(): void {
        if (!this.paused) {
            this.lastReceivedValue = null;
            this.paused = true;
        }
    }

    /**
     * Resume broadcasting
     */
    public resume(): void {
        if (this.paused) {
            if (this.lastReceivedValue !== null) {
                setTimeout(() => {
                    this.broadcastValue(this.lastReceivedValue);

                    this.paused = false;
                    this.lastReceivedValue = null;
                }, 1);
            } else {
                this.paused = false;
                this.lastReceivedValue = null;
            }
        }
    }

    /**
     * Completes all observers and stops
     * the store subscription
     */
    public terminate(): void {
        this.unplugFromStore();
        this.observers.forEach((obs: Observer<DependencyItem<T>>) => obs.complete());
        this.observers = [];
    }

    /**
     * Completly destroies this instance
     */
    public destroy(removeFromInstances = true): void {
        this.terminate();
        this.dependency.observable = null;
        this.dependency = null;
        if (removeFromInstances) {
            const index = DependencyObservable.instances.findIndex((currentValue, _index, _arr) => {
                return currentValue === this;
            });

            delete DependencyObservable.instances[index];
        }
    }

    /**
     * Unsubscribe from changes in the store
     */
    private unplugFromStore() {
        if (this.mainSubject) {
            this.mainSubject.next();
            this.mainSubject.complete();
        }
    }
}
